import type { INavBar } from './NavBar'

import { styled } from '@linaria/react'
import { PrimaryLoginButtonSC } from 'atoms/Button/Templates'
import { IconSC } from 'atoms/Icon'
import { PosterSC } from 'atoms/ImagePoster'
import { NavDirection, NavTabLink, NavTabSC } from 'atoms/NavTab'
import { TextBoxSC } from 'atoms/TextBox'
import { BrandContainerSC } from 'blocks/BrandContainer'
import { DropdownBody } from 'blocks/Dropdown'
import { DropdownHeaderStyled } from 'blocks/Dropdown/DropdownHeader'
import { DropdownItemTypeDivStyled } from 'blocks/Dropdown/DropdownItems/DropdownItemTypeDiv'
import { DropdownItemsGroupStyled } from 'blocks/Dropdown/DropdownItemsGroup'
import {
  DropdownAccountTemplate,
  DropdownBrowseTemplate,
} from 'blocks/Dropdown/Templates'
import { NavGroupWithMoreSC } from 'blocks/NavGroup/NavGroup.styled'
import {
  FIXED_INDENTS,
  INDENTS,
  LAYOUT,
  MEDIA,
  NAVBAR_HEIGHT,
  PAGE_NOTIFICATION,
} from 'vars/index'
import { NavBarAlignItems, NavBarFormation } from './NavBar.h'

export const IconContainerSC = styled.div``

export const WrapperIcons = styled.div``

export const NavBarSC = styled.div<
  Omit<INavBar, 'color'> & {
    colorDefault?: INavBar['color']
    isOpened: boolean
  }
>`
  ${MEDIA.MIN_768} {
    padding: ${({ direction }) =>
      direction === NavDirection.ROW
        ? `0 var(${LAYOUT.HorizontalPaddings})`
        : `0`};
  }
  --widthVerticalNavBar: 224px;
  display: flex;
  position: fixed;
  top: var(${PAGE_NOTIFICATION.HEIGHT}, 0px);
  left: 0;
  z-index: 7;
  flex-direction: ${({ direction }) => direction || NavDirection.ROW};
  flex-wrap: nowrap;
  align-items: ${({ isOpened, formation }) =>
    isOpened && formation === NavBarFormation.Dynamic
      ? 'flex-start'
      : 'center'};
  justify-content: space-between;
  width: ${({ direction }) =>
    direction === NavDirection.ROW ? '100vw' : 'max-content'};
  height: ${({ direction }) =>
    direction === NavDirection.COLUMN
      ? '100vh'
      : `var(${NAVBAR_HEIGHT.DEFAULT})`};
  background-color: ${({ background }) => `var(${background})`};
  padding: ${({ direction }) =>
    direction === NavDirection.ROW
      ? `0 var(${FIXED_INDENTS.INDENT_30}) 0 0`
      : `0`};

  & ${BrandContainerSC} {
    min-height: ${({ direction }) =>
      direction === NavDirection.COLUMN ? '64px' : 'auto'};
  }

  & ${PosterSC} {
    display: ${({ isOpened, direction }) =>
      isOpened && direction === NavDirection.COLUMN
        ? 'flex'
        : direction === NavDirection.ROW
          ? 'flex'
          : 'none'};
  }

  & > ${NavGroupWithMoreSC} {
    margin: ${({ alignItems, isOpened, direction }) =>
      isOpened && direction === NavDirection.COLUMN
        ? 0
        : alignItems === NavBarAlignItems.Start
          ? '0 auto 0 3.5rem'
          : alignItems === NavBarAlignItems.End
            ? '0 3.5rem 0 auto'
            : '0 auto'};
    flex-direction: ${({ direction }) => direction || NavDirection.ROW};
    justify-content: ${({ direction, alignItems }) =>
      alignItems === NavBarAlignItems.Start || direction === NavDirection.COLUMN
        ? 'flex-start'
        : alignItems === NavBarAlignItems.End
          ? 'flex-end'
          : 'center'};
    overflow-x: visible;

    & ${NavTabSC} {
      color: ${({ colorDefault }) =>
        colorDefault ? `var(${colorDefault})` : 'inherit'};
      width: ${({ direction, formation, isOpened }) =>
        direction === NavDirection.ROW
          ? 'max-content'
          : formation === NavBarFormation.Dynamic && isOpened
            ? '100%'
            : 'max-content'};

      & ${NavTabLink} {
        min-width: ${({ formation, isOpened, width }) =>
          formation === NavBarFormation.Dynamic && isOpened
            ? `${width}px`
            : '100%'};
      }

      ${IconSC} {
        margin: ${({ isOpened, formation }) =>
          isOpened && formation === NavBarFormation.Dynamic
            ? `0 var(${INDENTS.INDENT_35}) 0 0`
            : `0`};
      }

      & ${TextBoxSC} {
        display: ${({ isOpened, direction }) =>
          isOpened && direction === NavDirection.COLUMN
            ? 'flex'
            : direction === NavDirection.ROW
              ? 'flex'
              : 'none'};
      }
    }

    & ${DropdownHeaderStyled} {
      color: ${({ colorDefault }) => `var(${colorDefault})`};

      & ${IconSC} {
        margin: ${({ isOpened, formation }) =>
          isOpened && formation === NavBarFormation.Dynamic
            ? `0 var(${INDENTS.INDENT_35}) 0 0`
            : `0`};
      }

      & ${TextBoxSC} {
        display: ${({ isOpened, direction }) =>
          isOpened && direction === NavDirection.COLUMN
            ? 'flex'
            : direction === NavDirection.ROW
              ? 'flex'
              : 'none'};
      }
    }

    & ${DropdownBody} {
      z-index: 3;
    }

    & ${DropdownItemsGroupStyled} {
      background-color: ${({ background }) => `var(${background})`};
    }
  }

  ${DropdownAccountTemplate} {
    order: 3;
  }

  /* Styles for login button */
  ${PrimaryLoginButtonSC} {
    order: 3;
  }

  & ${DropdownBody} {
    & .active {
      & ${TextBoxSC} {
        color: ${({ colorSelected }) => `var(${colorSelected})`};
      }
    }
  }

  ${DropdownBrowseTemplate} {
    z-index: 3;

    ${MEDIA.MIN_1024} {
      flex-basis: 30vw;
    }

    ${MEDIA.MIN_1240} {
      flex-basis: 16.2vw;
    }

    ${DropdownHeaderStyled} {
      ${TextBoxSC} {
        word-break: initial;
      }
    }

    ${DropdownBody} {
      left: 15px;
      max-height: 90vh;
      padding-top: 0;

      ${DropdownItemsGroupStyled} {
        display: flex;

        ${MEDIA.MIN_1240} {
          &[data-multiColumns='true'] {
            display: grid;
            grid-template-areas: 'a a';
            grid-auto-rows: min-content;
          }
        }

        ${TextBoxSC}:first-child {
          grid-column: 1 / span 2;
        }

        & ${DropdownItemTypeDivStyled} {
          width: 100%;

          ${MEDIA.MIN_600} {
            width: 200px;
          }

          ${MEDIA.MIN_1240} {
            width: 224px;
          }
        }
      }
    }
  }
`

export const NavBarLogoSC = styled.div`
  ${BrandContainerSC} {
    position: absolute;
    top: var(${FIXED_INDENTS.INDENT_50});

    ${MEDIA.MAX_599} {
      left: var(${FIXED_INDENTS.INDENT_40});
    }

    ${MEDIA.MIN_600} {
      left: var(${FIXED_INDENTS.INDENT_100});
    }

    ${MEDIA.MIN_1440} {
      left: var(${FIXED_INDENTS.INDENT_140});
    }
  }
`
